import React, { useEffect, useState, useRef, useCallback } from 'react';
import { FaPaintBrush, FaTools, FaRocket, FaPlay } from 'react-icons/fa';
import AOS from 'aos';
import 'aos/dist/aos.css';

// 3 feature boxes
const chooseItems = [
  {
    Icon: FaPaintBrush,
    title: "Design Driven Excellence",
    desc: "We merge art and functionality to create impactful visual experiences. From exclusive prints to immersive wall graphics, our designs elevate both modern homes and commercial spaces.",
  },
  {
    Icon: FaTools,
    title: "Customized for You",
    desc: "We don’t believe in one size fits all. Whether it's a bespoke wallpaper, a hand painted canvas, or a custom graphic requirement, we design around your space, style, and story.",
  },
  {
    Icon: FaRocket,
    title: "Concept to Execution",
    desc: "From idea to installation, we handle the complete journey. With detailed planning, previews, and on site execution, we bring your vision to life flawlessly and efficiently.",
  },
];

// 5 Reels Videos from S3
const reelsData = [
  {
    id: 1,
    url: "https://vizaura.s3.eu-north-1.amazonaws.com/Reels/(1).mp4",
  },
  {
    id: 2,
    url: "https://vizaura.s3.eu-north-1.amazonaws.com/Reels/(2).mp4",
  },
  {
    id: 3,
    url: "https://vizaura.s3.eu-north-1.amazonaws.com/Reels/(3).mp4",
  },
  {
    id: 4,
    url: "https://vizaura.s3.eu-north-1.amazonaws.com/Reels/(4).mp4",
  },
  {
    id: 5,
    url: "https://vizaura.s3.eu-north-1.amazonaws.com/Reels/(5).mp4",
  },
];

// Reel Card Component with Video
const ReelCard = ({ reel, isMobile, onEnded }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);

  const togglePlay = (e) => {
    e.stopPropagation();
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play().catch(() => {});
        setIsPlaying(true);
      } else {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    }
  };

  const handleEnded = () => {
    if (isMobile && onEnded) {
      onEnded();
    } else if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {});
    }
  };

  return (
    <div 
      className="relative w-full aspect-[9/16] max-h-[520px] overflow-hidden rounded-2xl bg-black select-none cursor-pointer"
      onClick={togglePlay}
    >
      <video
        ref={videoRef}
        src={reel.url}
        className="w-full h-full object-cover rounded-2xl"
        autoPlay
        loop={!isMobile}
        muted
        playsInline
        preload="metadata"
        onEnded={handleEnded}
      />

      {/* Play / Pause Indicator on Hover or Paused */}
      {!isPlaying && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-xs">
          <div className="w-14 h-14 rounded-full bg-white/90 text-gray-900 flex items-center justify-center shadow-xl transform scale-110 transition-transform">
            <FaPlay className="ml-1 text-lg" />
          </div>
        </div>
      )}
    </div>
  );
};

const ChooseUS = () => {
  const [isMobile, setIsMobile] = useState(false);
  const scrollContainerRef = useRef(null);

  // Initialize AOS and handle resize
  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
      easing: 'ease-out',
    });

    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // When a reel completes on mobile, smoothly swipe to the next reel
  const handleReelEnded = useCallback((index) => {
    if (!isMobile) return;
    const container = scrollContainerRef.current;
    if (!container) return;

    const cards = container.querySelectorAll('.choose-reel-card');
    const nextIndex = (index + 1) % reelsData.length;

    if (cards[nextIndex]) {
      const firstCard = cards[0];
      const cardWidth = firstCard.getBoundingClientRect().width;
      const gap = 24; // Corresponds to gap-6
      const targetScrollLeft = nextIndex === 0 ? 0 : (cardWidth + gap) * nextIndex;

      container.scrollTo({
        left: targetScrollLeft,
        behavior: 'smooth',
      });

      // Ensure the next video starts playing from beginning
      const nextVideo = cards[nextIndex].querySelector('video');
      if (nextVideo) {
        nextVideo.currentTime = 0;
        nextVideo.play().catch(() => {});
      }
    }
  }, [isMobile]);

  // Autoplay the currently centered reel on mobile when user scrolls
  useEffect(() => {
    if (!isMobile) return;
    const container = scrollContainerRef.current;
    if (!container) return;

    const cards = container.querySelectorAll('.choose-reel-card');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target.querySelector('video');
          if (!video) return;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            video.play().catch(() => {});
          } else if (entry.intersectionRatio < 0.3) {
            video.pause();
          }
        });
      },
      {
        root: container,
        threshold: [0.3, 0.6],
      }
    );

    cards.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, [isMobile]);

  // Scroll left and right functions
  const scrollLeft = () => {
    const container = scrollContainerRef.current;
    if (container) {
      const firstCard = container.querySelector('.choose-reel-card');
      if (firstCard) {
        const cardWidth = firstCard.getBoundingClientRect().width;
        const gap = 24; // Corresponds to gap-6
        const scrollAmount = cardWidth + gap;
        container.scrollBy({
          left: -scrollAmount,
          behavior: 'smooth',
        });
      }
    }
  };

  const scrollRight = () => {
    const container = scrollContainerRef.current;
    if (container) {
      const firstCard = container.querySelector('.choose-reel-card');
      if (firstCard) {
        const cardWidth = firstCard.getBoundingClientRect().width;
        const gap = 24; // Corresponds to gap-6
        const scrollAmount = cardWidth + gap;
        container.scrollBy({
          left: scrollAmount,
          behavior: 'smooth',
        });
      }
    }
  };

  return (
    <section className="w-full bg-white py-16 sm:py-20 font-serif overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-14 group" data-aos="fade-up">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-gray-800 tracking-tight">
            Why Choose Vizaura?
          </h2>
          <p className="text-gray-500 mt-3 text-base sm:text-lg max-w-2xl mx-auto font-sans font-light">
            Transform your spaces with designer wallcoverings, curated art prints, and tailored bespoke executions.
          </p>
        </div>

        {/* 1. Choose 3 Boxes in the Same Row (Compact height) */}
        <div 
          className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mb-12 sm:mb-14"
          data-aos="fade-up"
          data-aos-delay="100"
        >
          {chooseItems.map((item, index) => {
            const IconComponent = item.Icon;
            return (
              <div
                key={index}
                className="bg-gray-50/90 hover:bg-white rounded-xl p-4 sm:p-5 border border-gray-200/80 hover:border-yellow-400 hover:shadow-lg hover:shadow-yellow-100/30 transition-all duration-300 flex flex-col items-start group"
              >
                <div className="h-9 w-9 rounded-lg bg-yellow-100/70 border border-yellow-200 flex items-center justify-center text-yellow-700 text-base mb-3 group-hover:scale-105 group-hover:bg-yellow-500 group-hover:text-white transition-all duration-300">
                  <IconComponent />
                </div>
                <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-1.5 font-serif group-hover:text-yellow-700 transition-colors">
                  {item.title}
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 leading-relaxed font-sans font-light">
                  {item.desc}
                </p>
              </div>
            );
          })}
        </div>

        {/* 2. Reels Videos Slider Section (matching Thirdslide responsive pattern) */}
        <div className="mt-6" data-aos="fade-up" data-aos-delay="200">
          <div className="text-center mb-8">
            <h3 className="text-2xl sm:text-3xl font-serif text-gray-800">
              Art & Stories in Motion
            </h3>
            <p className="text-gray-500 text-sm sm:text-base mt-2 font-sans font-light">
              Explore our wallpapers, fine art prints, live installations, and creative journeys.
            </p>
          </div>

          <div className="relative max-w-7xl mx-auto">
            {/* Scrollable container for reels matching Thirdslide pattern */}
            <div
              ref={scrollContainerRef}
              className="flex overflow-x-auto gap-6 mt-6 px-4 pb-4 no-scrollbar md:grid md:grid-flow-col md:auto-cols-[calc((100%/3)-16px)] lg:auto-cols-[calc((100%/4)-18px)]"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              {reelsData.map((reel, index) => (
                <div
                  key={reel.id}
                  className="w-full flex-shrink-0 bg-black rounded-2xl shadow-xl hover:shadow-2xl overflow-hidden transform transition-all duration-300 hover:-translate-y-2 flex flex-col relative group choose-reel-card"
                  data-aos="fade-up"
                  data-aos-delay={200 + index * 100}
                >
                  <ReelCard
                    reel={reel}
                    isMobile={isMobile}
                    onEnded={() => handleReelEnded(index)}
                  />
                </div>
              ))}
            </div>

            {/* Desktop Scroll Left Button - visible on medium and larger screens */}
            <button
              onClick={scrollLeft}
              className="absolute -left-16 top-1/2 -translate-y-1/2 bg-gray-800 text-white p-3 rounded-full shadow-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 z-10 hidden md:block opacity-70 hover:opacity-100 transition-opacity duration-300"
              aria-label="Scroll left"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            {/* Desktop Scroll Right Button - visible on medium and larger screens */}
            <button
              onClick={scrollRight}
              className="absolute -right-16 top-1/2 -translate-y-1/2 bg-gray-800 text-white p-3 rounded-full shadow-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 z-10 hidden md:block opacity-70 hover:opacity-100 transition-opacity duration-300"
              aria-label="Scroll right"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {/* Mobile Scroll Left Button - visible only on small screens */}
            <button
              onClick={scrollLeft}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-gray-800 text-white p-2 rounded-full shadow-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 z-10 md:hidden opacity-70 hover:opacity-100 transition-opacity duration-300"
              aria-label="Scroll left"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            {/* Mobile Scroll Right Button - visible only on small screens */}
            <button
              onClick={scrollRight}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-gray-800 text-white p-2 rounded-full shadow-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 z-10 md:hidden opacity-70 hover:opacity-100 transition-opacity duration-300"
              aria-label="Scroll right"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

      </div>
    </section>
  );
};

export default ChooseUS;
